import { motion } from 'framer-motion';

// Fade animations
export const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.3 },
};

export const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.4, ease: [0.34, 1.56, 0.64, 1] },
};

export const fadeInDown = {
  initial: { opacity: 0, y: -20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 20 },
  transition: { duration: 0.4, ease: [0.34, 1.56, 0.64, 1] },
};

export const fadeInLeft = {
  initial: { opacity: 0, x: -20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 20 },
  transition: { duration: 0.4, ease: [0.34, 1.56, 0.64, 1] },
};

export const fadeInRight = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: { duration: 0.4, ease: [0.34, 1.56, 0.64, 1] },
};

// Scale animations
export const scaleIn = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.9 },
  transition: { duration: 0.3, ease: [0.34, 1.56, 0.64, 1] },
};

export const scaleInCenter = {
  initial: { opacity: 0, scale: 0.5 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.5 },
  transition: { duration: 0.4, ease: [0.34, 1.56, 0.64, 1] },
};

// Slide animations
export const slideInUp = {
  initial: { y: '100%' },
  animate: { y: 0 },
  exit: { y: '100%' },
  transition: { duration: 0.4, ease: [0.34, 1.56, 0.64, 1] },
};

export const slideInDown = {
  initial: { y: '-100%' },
  animate: { y: 0 },
  exit: { y: '-100%' },
  transition: { duration: 0.4, ease: [0.34, 1.56, 0.64, 1] },
};

export const slideInLeft = {
  initial: { x: '-100%' },
  animate: { x: 0 },
  exit: { x: '-100%' },
  transition: { duration: 0.4, ease: [0.34, 1.56, 0.64, 1] },
};

export const slideInRight = {
  initial: { x: '100%' },
  animate: { x: 0 },
  exit: { x: '100%' },
  transition: { duration: 0.4, ease: [0.34, 1.56, 0.64, 1] },
};

// Stagger animations
export const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

export const staggerItem = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: [0.34, 1.56, 0.64, 1] },
};

// Page transitions
export const pageTransition = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.4, ease: [0.34, 1.56, 0.64, 1] },
};

// Card hover animations
export const cardHover = {
  rest: { scale: 1, y: 0 },
  hover: { scale: 1.02, y: -4 },
  transition: { duration: 0.3, ease: 'easeOut' },
};

// Button animations
export const buttonTap = {
  scale: 0.98,
  transition: { duration: 0.1 },
};

// Spring animations
export const springConfig = {
  type: 'spring',
  stiffness: 500,
  damping: 30,
};

export const springSoft = {
  type: 'spring',
  stiffness: 300,
  damping: 25,
};

// Animation components
export const AnimatedContainer = motion.div;

export const FadeIn = ({ children, delay = 0, direction = 'up', ...props }) => {
  const directions = {
    up: { y: 20 },
    down: { y: -20 },
    left: { x: 20 },
    right: { x: -20 },
  };

  return (
    <motion.div
      initial={{ opacity: 0, ...directions[direction] }}
      animate={{ opacity: 1, x: 0, y: 0 }}
      transition={{ 
        duration: 0.4, 
        delay,
        ease: [0.34, 1.56, 0.64, 1] 
      }}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export const ScaleIn = ({ children, delay = 0, ...props }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ 
      duration: 0.3, 
      delay,
      ease: [0.34, 1.56, 0.64, 1] 
    }}
    {...props}
  >
    {children}
  </motion.div>
);

export const StaggerContainer = ({ children, ...props }) => (
  <motion.div
    initial="initial"
    animate="animate"
    variants={staggerContainer}
    {...props}
  >
    {children}
  </motion.div>
);

export const StaggerItem = ({ children, ...props }) => (
  <motion.div variants={staggerItem} {...props}>
    {children}
  </motion.div>
);

// Number counter animation
export const AnimatedNumber = ({ value, duration = 1, prefix = '', suffix = '' }) => {
  return (
    <motion.span
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {prefix}
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration }}
      >
        {value.toLocaleString('pt-BR')}
      </motion.span>
      {suffix}
    </motion.span>
  );
};

// Loading skeleton animation
export const SkeletonPulse = ({ className }) => (
  <motion.div
    className={`bg-white/10 rounded-lg ${className}`}
    animate={{ opacity: [0.5, 0.8, 0.5] }}
    transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
  />
);

// Floating animation
export const Floating = ({ children, duration = 6, distance = 20, ...props }) => (
  <motion.div
    animate={{ y: [0, -distance, 0] }}
    transition={{ duration, repeat: Infinity, ease: 'easeInOut' }}
    {...props}
  >
    {children}
  </motion.div>
);

// Pulse glow animation
export const PulseGlow = ({ children, color = 'rgba(99, 102, 241, 0.5)', ...props }) => (
  <motion.div
    animate={{ 
      boxShadow: [
        `0 0 20px ${color}`,
        `0 0 40px ${color}`,
        `0 0 20px ${color}`,
      ]
    }}
    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
    {...props}
  >
    {children}
  </motion.div>
);

// Ripple effect
export const Ripple = ({ children, ...props }) => {
  return (
    <motion.div
      whileTap={{
        scale: 0.98,
        transition: { duration: 0.1 },
      }}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export default {
  fadeIn,
  fadeInUp,
  fadeInDown,
  fadeInLeft,
  fadeInRight,
  scaleIn,
  scaleInCenter,
  slideInUp,
  slideInDown,
  slideInLeft,
  slideInRight,
  staggerContainer,
  staggerItem,
  pageTransition,
  cardHover,
  buttonTap,
  springConfig,
  springSoft,
};
